import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Truck, Wrench, ShoppingCart, type LucideIcon } from "lucide-react";
import { SegmentIllustration } from "./SegmentIllustrations";
import { useLanguage } from "@/hooks/useLanguage";

const segments: { key: "transport" | "service" | "ecommerce"; number: string; href: string; icon: LucideIcon }[] = [
  {
    key: "transport",
    number: "01",
    href: "/losningar/transport",
    icon: Truck,
  },
  {
    key: "service",
    number: "02",
    href: "/losningar/service",
    icon: Wrench,
  },
  {
    key: "ecommerce",
    number: "03",
    href: "/losningar/ehandel",
    icon: ShoppingCart,
  },
];

export function SegmentsSection() {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguage();

  return (
    <section className="py-12 lg:py-32 bg-background min-h-[300px]">
      <div className="container mx-auto px-4 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-10 lg:mb-20"
        >
          <h2 className="text-3xl md:text-4xl font-medium font-heading text-primary tracking-tight">
            {t("segments.title")}
          </h2>
          <p className="mt-4 text-lg text-muted-foreground font-body">
            {t("segments.subtitle")}
          </p>
        </motion.div>

        <div className="space-y-14 lg:space-y-32">
          {segments.map((segment, index) => {
            const isReversed = index % 2 === 1;

            return (
              <div
                key={segment.key}
                className={`flex flex-col ${
                  isReversed ? "lg:flex-row-reverse" : "lg:flex-row"
                } gap-6 lg:gap-20 items-center`}
              >
              {/* Illustration - hidden on mobile, shown on desktop */}
                <motion.div
                  className="hidden lg:flex flex-1 w-full max-w-lg lg:max-w-none"
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: 0.1 }}
                >
                  <div className="bg-secondary/50 rounded-2xl p-6 lg:p-8 border border-border w-full">
                    <SegmentIllustration type={segment.key} />
                  </div>
                </motion.div>

                {/* Text */}
                <motion.div
                  className="flex-1"
                  initial={{
                    opacity: 0,
                    x: isReversed ? 30 : -30,
                  }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold text-coral tracking-widest uppercase font-subheading">
                      {segment.number}
                    </span>
                    {/* Mobile icon */}
                    <div className="lg:hidden w-10 h-10 rounded-xl bg-coral/10 flex items-center justify-center">
                      <segment.icon className="h-5 w-5 text-coral" />
                    </div>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-medium font-heading text-primary mt-2 tracking-tight">
                    {t(`segments.${segment.key}.title`)}
                  </h3>
                  <p className="text-muted-foreground font-body leading-relaxed mt-4 text-lg">
                    {t(`segments.${segment.key}.description`)}
                  </p>
                  <Link
                    to={getLocalizedPath(segment.href)}
                    className="inline-flex items-center mt-6 text-sm font-semibold text-coral hover:text-coral/80 transition-colors gap-1.5 group"
                  >
                    {t("segments.readMore", { defaultValue: "Läs mer" })} {t(`segments.${segment.key}.title`).toLowerCase()}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                  </Link>
                </motion.div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
